/**
 * Configuration for 10M Wealth Plan Funnel A
 * Target: High net worth individuals (5M+ business valuation or $500K+ annual revenue)
 */

import { FunnelConfig, colorPalettes, createTheme } from '@marketing-and-funnels/funnel-template';
import { dslSlides } from './generated-dsl-slides';

// Canadian Provinces
const CANADIAN_PROVINCES = [
  { value: 'AB', label: 'Alberta' },
  { value: 'BC', label: 'British Columbia' },
  { value: 'MB', label: 'Manitoba' },
  { value: 'NB', label: 'New Brunswick' },
  { value: 'NL', label: 'Newfoundland and Labrador' },
  { value: 'NS', label: 'Nova Scotia' },
  { value: 'NT', label: 'Northwest Territories' },
  { value: 'NU', label: 'Nunavut' },
  { value: 'ON', label: 'Ontario' },
  { value: 'PE', label: 'Prince Edward Island' },
  { value: 'QC', label: 'Quebec' },
  { value: 'SK', label: 'Saskatchewan' },
  { value: 'YT', label: 'Yukon' },
];

export const wealthPlanConfig: FunnelConfig = {
  name: '10M Wealth Plan Funnel A',
  description: 'Landing page for 10M wealth plan assessment - High net worth individuals',
  
  theme: createTheme('wealth-plan', {
    primary: '#1e3a8a', // navy blue - professional
    secondary: '#1e40af', // blue-800
    accent: '#d4af37', // gold - stands out on dark
    gold: '#d4af37', // gold
    emerald: '#10b981', // emerald-500 (success)
    background: '#0f172a', // slate-900 - dark background
    foreground: '#f8fafc', // slate-50 - light text
    muted: '#94a3b8', // slate-400
    border: '#334155', // slate-700
  }),
  
  media: {
    type: 'dsl',
    dsl: {
      slides: dslSlides,
      onStartAssessment: () => {
        // This will be overridden by the actual callback passed from FunnelHero
      },
    },
  },
  
  quiz: {
    questions: [
      {
        id: 'firstName',
        type: 'text',
        label: 'First Name',
        placeholder: 'Enter your first name',
        required: true,
      },
      {
        id: 'lastName',
        type: 'text',
        label: 'Last Name',
        placeholder: 'Enter your last name',
        required: true,
      },
      {
        id: 'email',
        type: 'email',
        label: 'Email Address',
        placeholder: 'Enter your email',
        required: true,
        validation: {
          pattern: '^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$',
        },
      },
      {
        id: 'phone',
        type: 'phone',
        label: 'Phone Number',
        placeholder: '(555) 123-4567',
        required: true,
      },
      {
        id: 'province',
        type: 'select',
        label: 'Which province are you located in?',
        placeholder: 'Select your province',
        required: true,
        options: CANADIAN_PROVINCES,
      },
      {
        id: 'qualification',
        type: 'radio',
        label: 'Which best describes your situation?',
        required: true,
        options: [
          { 
            value: 'business-valuation', 
            label: 'I own a business with $5M+ valuation and have trapped capital' 
          },
          { 
            value: 'annual-revenue', 
            label: 'I earn $500K+ annually (salary or dividends) and have corporate capital' 
          },
          { 
            value: 'incorporated-professional', 
            label: 'I am an incorporated professional (doctor, dentist, etc.) with retained earnings' 
          },
          { 
            value: 'neither', 
            label: 'Neither of the above (will be filtered out)' 
          },
        ],
        validation: {
          custom: (value) => {
            if (value === 'neither') {
              return 'This offer is specifically for business owners and incorporated professionals with trapped corporate capital. Please contact us directly for other opportunities.';
            }
            return true;
          },
        },
      },
      {
        id: 'businessValuation',
        type: 'radio',
        label: 'What is your approximate business valuation?',
        required: true,
        conditional: {
          dependsOn: 'qualification',
          showWhen: 'business-valuation',
        },
        options: [
          { value: '5m-10m', label: '$5M - $10M' },
          { value: '10m-25m', label: '$10M - $25M' },
          { value: '25m-50m', label: '$25M - $50M' },
          { value: '50m+', label: '$50M+' },
        ],
      },
      {
        id: 'annualRevenue',
        type: 'radio',
        label: 'What is your approximate annual revenue?',
        required: true,
        conditional: {
          dependsOn: 'qualification',
          showWhen: ['annual-revenue', 'incorporated-professional'],
        },
        options: [
          { value: '500k-1m', label: '$500K - $1M' },
          { value: '1m-2.5m', label: '$1M - $2.5M' },
          { value: '2.5m-5m', label: '$2.5M - $5M' },
          { value: '5m+', label: '$5M+' },
        ],
      },
    ],
    showProgress: true,
    inline: true,
  },
  
  ghl: {
    locationId: 'juHPJcxY9LffQPmN8qTi',
    apiKey: import.meta.env.VITE_GHL_API_KEY || '',
    fieldMapping: {
      firstName: 'firstName',
      lastName: 'lastName',
      email: 'email',
      phone: 'phone',
      province: 'custom_field_province',
      qualification: 'custom_field_qualification',
      businessValuation: 'custom_field_business_valuation',
      annualRevenue: 'custom_field_annual_revenue',
    },
    defaultTags: ['10m_wealth_plan_lead'],
    contactCreatedTags: ['10m_wealth_plan_qualified'],
    quizCompleteTags: ['10m_wealth_plan_quiz_complete'],
    bookingCompleteTags: ['10m_wealth_plan_appointment_booked'],
  },
  
  booking: {
    type: 'calcom',
    calcomApiKey: import.meta.env.VITE_CALCOM_API_KEY || 'cal_live_5e74957f47469578771bbc0a6761f551',
    // Map provinces to Cal.com event type IDs
    // Event Type 3870181: Ontario, Quebec, New Brunswick, Nova Scotia, Prince Edward Island
    // Event Type 3870185: Manitoba, Saskatchewan, Northwest Territories (Prairies)
    // Event Type 3870170: British Columbia, Alberta
    provinceEventTypeMap: {
      'AB': '3870170', // Alberta → BC & AB event type
      'BC': '3870170', // British Columbia → BC & AB event type
      'MB': '3870185', // Manitoba → Prairies event type
      'NB': '3870181', // New Brunswick → ON & Eastern event type
      'NL': '3870181', // Newfoundland and Labrador → Default to ON & Eastern
      'NS': '3870181', // Nova Scotia → ON & Eastern event type
      'NT': '3870185', // Northwest Territories → Prairies event type
      'NU': '3870181', // Nunavut → Default to ON & Eastern
      'ON': '3870181', // Ontario → ON & Eastern event type
      'PE': '3870181', // Prince Edward Island → ON & Eastern event type
      'QC': '3870181', // Quebec → ON & Eastern event type
      'SK': '3870185', // Saskatchewan → Prairies event type
      'YT': '3870181', // Yukon → Default to ON & Eastern
    },
    // Default event type ID (fallback if province not mapped)
    calcomEventTypeId: '3870181', // Default to ON & Eastern event type
    apiVersion: 'v2', // Use Cal.com V2 API
    webhookUrl: import.meta.env.VITE_CALCOM_WEBHOOK_URL,
  },
  
  successActions: [
    {
      type: 'ghl-tags',
      tags: ['10m_wealth_plan_appointment_booked'],
    },
    {
      type: 'success-page',
      route: '/confirmed-booking',
    },
  ],
  
  meta: {
    title: 'Build a $10M Wealth Plan - Access Trapped Corporate Capital Tax-Efficiently',
    description: 'Learn how to withdraw capital from your corporation without triggering huge taxes. For business owners and incorporated professionals with trapped corporate capital.',
  },
  
  analytics: {
    metaCAPI: {
      enabled: true,
    },
  },
};
